import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LANDING } from '@/constants/testIds';
import { scrollToId } from '@/hooks/useLenis';
import Logo from './Logo';

const links = [
    { id: 'perfiles', label: 'Para quién', testId: LANDING.navLinkMethod },
    { id: 'fases', label: 'Cómo funciona', testId: LANDING.navLinkPhases },
    { id: 'inversion', label: 'Precios', testId: LANDING.navLinkPricing },
    { id: 'faq', label: 'FAQ', testId: LANDING.navLinkContact },
];

const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    return (
        <>
            <motion.header
                initial={{ y: -60, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.9, delay: 0.1 }}
                className="fixed top-0 left-0 right-0 z-50"
            >
                <div className={`transition-all duration-500 ${scrolled ? 'border-b border-white/8 bg-black/60 backdrop-blur-xl' : 'bg-transparent'}`}>
                    <nav className="mx-auto flex max-w-[1440px] items-center justify-between px-6 md:px-10 py-4">
                        <button
                            data-testid={LANDING.navLogo}
                            onClick={() => scrollToId('hero')}
                            className="flex items-center gap-3"
                        >
                            <Logo size={46} />
                        </button>

                        <div className="hidden md:flex items-center gap-8">
                            {links.map((l) => (
                                <button
                                    key={l.id}
                                    data-testid={l.testId}
                                    onClick={() => scrollToId(l.id)}
                                    className="link-underline text-[11px] uppercase tracking-[0.2em] font-semibold text-white/75 hover:text-white"
                                >
                                    {l.label}
                                </button>
                            ))}
                        </div>

                        <button
                            data-testid={LANDING.navCtaBook}
                            onClick={() => scrollToId('sesion')}
                            className="hidden md:inline-flex items-center gap-2 rounded-full bg-[color:var(--brand)] px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.16em] text-white hover:brightness-110 transition"
                        >
                            Agendar sesión
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>

                        <button
                            aria-label="Abrir menú"
                            onClick={() => setOpen((v) => !v)}
                            className="md:hidden flex flex-col gap-[5px] p-2"
                        >
                            <span className={`h-[2px] w-6 bg-white transition-transform ${open ? 'translate-y-[7px] rotate-45' : ''}`} />
                            <span className={`h-[2px] w-6 bg-white transition-opacity ${open ? 'opacity-0' : 'opacity-100'}`} />
                            <span className={`h-[2px] w-6 bg-white transition-transform ${open ? '-translate-y-[7px] -rotate-45' : ''}`} />
                        </button>
                    </nav>
                </div>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-40 md:hidden bg-black/96 backdrop-blur-2xl pt-24 px-6"
                    >
                        <div className="flex flex-col gap-6">
                            {links.map((l, i) => (
                                <motion.button
                                    key={l.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.05 + i * 0.06 }}
                                    onClick={() => {
                                        setOpen(false);
                                        setTimeout(() => scrollToId(l.id), 200);
                                    }}
                                    className="text-left font-display text-4xl text-white"
                                >
                                    {l.label}
                                </motion.button>
                            ))}
                            <motion.button
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.4 }}
                                onClick={() => {
                                    setOpen(false);
                                    setTimeout(() => scrollToId('sesion'), 200);
                                }}
                                className="mt-6 rounded-full bg-[color:var(--brand)] px-6 py-4 text-sm font-bold uppercase tracking-[0.16em] text-white"
                            >
                                Agendar sesión →
                            </motion.button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Nav;
